import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, Input, Modal, PageHeader, Pill, Select } from "@/components/ui";
import { popup, confirmPopup } from "@/components/popup";

interface ContentItem {
  key: string;
  lang: string;
  default_value: string;
  value: string | null;
  media_url: string | null;
  overridden: boolean;
  updated_at: string | null;
}
interface ContentList {
  items: ContentItem[];
  langs: string[];
}

const GROUP_TITLES: Record<string, string> = {
  welcome: "Приветствие",
  menu: "Меню",
  start: "Старт",
  test: "Тестовый доступ",
  buy: "Покупка",
  topup: "Пополнение",
  access: "Мой доступ",
  profile: "Профиль",
  referral: "Рефералка",
  support: "Поддержка",
  about: "О нас",
  faq: "FAQ",
  vpn: "VPN",
  payment: "Оплата",
  promo: "Промокоды",
  back: "Навигация",
  lang: "Язык",
  admin: "Уведомления",
};

function groupOf(key: string): string {
  return key.split(/[._]/)[0] || "прочее";
}
function isButton(key: string, text: string): boolean {
  return /_btn$|\.btn|button|menu\.|back_to_menu|\.back/.test(key) || text.length < 40;
}

function renderTelegramHtml(src: string): string {
  let s = src.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const tags = ["b", "strong", "i", "em", "u", "s", "code", "pre", "blockquote", "tg-spoiler"];
  for (const tg of tags) {
    s = s.replace(new RegExp(`&lt;${tg}&gt;`, "g"), `<${tg}>`).replace(new RegExp(`&lt;/${tg}&gt;`, "g"), `</${tg}>`);
  }
  s = s.replace(/&lt;a href=(?:&quot;|")(.*?)(?:&quot;|")&gt;(.*?)&lt;\/a&gt;/g, '<a href="$1" class="tg-link">$2</a>');
  s = s.replace(/\{[a-zA-Z0-9_]+\}/g, '<span class="tg-ph">$&</span>');
  return s.replace(/\n/g, "<br/>");
}

function TgPreview({ text, media, btn }: { text: string; media?: string | null; btn: boolean }) {
  if (btn)
    return (
      <div className="inline-flex items-center rounded-lg bg-[#2b5278] text-zinc-50 text-sm px-3.5 py-2 max-w-full">
        <span className="tg-preview truncate" dangerouslySetInnerHTML={{ __html: renderTelegramHtml(text) }} />
      </div>
    );
  return (
    <div className="rounded-2xl rounded-tl-sm bg-[#182533] border border-white/[0.05] px-3.5 py-2.5 text-sm text-zinc-100 leading-relaxed break-words tg-preview">
      {media && (
        <div className="mb-2 rounded-lg overflow-hidden bg-black/30 border border-white/[0.06] aspect-[16/9] flex items-center justify-center text-zinc-600 text-xs">
          🖼 баннер
        </div>
      )}
      <span dangerouslySetInnerHTML={{ __html: renderTelegramHtml(text) }} />
    </div>
  );
}

interface ScreenText {
  key: string;
  default_value: string;
  value: string | null;
  overridden: boolean;
  current: string;
}
interface ScreenButton {
  label_key: string;
  callback: string | null;
  to: string | null;
  url: boolean;
  style: string | null;
  target: boolean;
  label: ScreenText;
}
interface ScreenItem {
  key: string;
  banner: string;
  parent: string | null;
  caption: ScreenText;
  buttons: ScreenButton[];
}

const SCREEN_TITLES: Record<string, string> = {
  main: "Главное меню",
  access: "Мой доступ",
  proxy: "Прокси",
  vpn: "VPN",
  buy: "Купить подписку",
  tariffs: "Тарифы",
  profile: "Аккаунт",
  pay: "Пополнение",
  invites: "Приглашения",
  support: "Поддержка",
  history: "История операций",
  about: "О сервисе",
  faq: "Частые вопросы",
  test: "Тестовый доступ (архив)",
};

/** Вкладка «Экраны» (ТЗ-05 §3.1): навигация как её видит юзер, а не 230 ключей. */
function ScreensTab({ onEdit }: { onEdit: (key: string) => void }) {
  const [active, setActive] = useState<string | null>(null);
  const sendSelf = useMutation({
    mutationFn: (key: string) =>
      api(`/api/admin/bot-content/preview/${key}`, { method: "POST" }),
    onSuccess: () =>
      popup({ tone: "success", title: "Отправлено", body: "Экран пришёл вам в бот." }),
    onError: (e) => popup({ tone: "error", title: "Превью", body: (e as Error).message }),
  });
  const q = useQuery<{ screens: ScreenItem[] }>({
    queryKey: ["bot-content-screens"],
    queryFn: () => api("/api/admin/bot-content/screens"),
  });
  const screens = q.data?.screens ?? [];
  const cur = screens.find((s) => s.key === active) ?? screens[0] ?? null;

  if (q.isLoading) return <div className="text-zinc-400">Загрузка…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-5">
      <nav className="space-y-1">
        {screens.map((s) => {
          const changed = s.caption.overridden || s.buttons.some((b) => b.label.overridden);
          return (
            <button
              key={s.key}
              onClick={() => setActive(s.key)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center justify-between gap-2 ${
                cur?.key === s.key
                  ? "bg-brand-soft text-brand glow-ring"
                  : "text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.04]"
              }`}
            >
              <span className="truncate">{SCREEN_TITLES[s.key] ?? s.key}</span>
              {changed && <span className="w-1.5 h-1.5 rounded-full bg-brand shrink-0" />}
            </button>
          );
        })}
      </nav>

      {cur && (
        <div className="space-y-4">
          <div className="text-xs text-zinc-500">
            баннер <code className="text-zinc-400">{cur.banner}</code>
            {cur.parent && (
              <>
                {" · "}назад в <code className="text-zinc-400">{SCREEN_TITLES[cur.parent] ?? cur.parent}</code>
              </>
            )}
          </div>

          {/* Превью экрана целиком — как его увидит юзер */}
          <div className="max-w-md space-y-2">
            <button
              onClick={() => onEdit(cur.caption.key)}
              className="w-full text-left group"
              title="Изменить текст экрана"
            >
              <div className="rounded-2xl rounded-tl-sm bg-[#182533] border border-white/[0.05] group-hover:border-brand/40 transition-colors px-3.5 py-2.5 text-sm text-zinc-100 leading-relaxed break-words tg-preview">
                <div className="mb-2 rounded-lg overflow-hidden bg-black/30 border border-white/[0.06] aspect-[16/9] flex items-center justify-center text-zinc-600 text-xs">
                  🖼 {cur.banner}
                </div>
                <span dangerouslySetInnerHTML={{ __html: renderTelegramHtml(cur.caption.current) }} />
              </div>
            </button>

            {cur.buttons.map((b) => (
              <button
                key={b.label_key}
                onClick={() => onEdit(b.label.key)}
                className="w-full group"
                title="Изменить текст кнопки"
              >
                <div
                  className={`rounded-lg text-sm px-3.5 py-2 text-center transition-colors border ${
                    b.style === "primary" || b.target
                      ? "bg-[#2b5278] text-zinc-50 border-transparent group-hover:border-brand/60"
                      : "bg-[#212d3b] text-zinc-200 border-transparent group-hover:border-brand/60"
                  }`}
                >
                  <span
                    className="tg-preview"
                    dangerouslySetInnerHTML={{ __html: renderTelegramHtml(b.label.current) }}
                  />
                </div>
              </button>
            ))}
            {cur.buttons.length === 0 && (
              <p className="text-xs text-zinc-600">
                Кнопки этого экрана собираются в коде динамически (тарифы, списки) — правьте их во
                вкладке «Все тексты».
              </p>
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Button
              variant="ghost"
              disabled={sendSelf.isPending}
              onClick={() => sendSelf.mutate(cur.key)}
            >
              {sendSelf.isPending ? "Отправляю…" : "Отправить себе в бот"}
            </Button>
            <p className="text-xs text-zinc-600">
              Клик по тексту или кнопке в превью открывает редактор. «Отправить себе» покажет
              экран ровно так, как его увидит юзер.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

interface BannerItem {
  key: string;
  custom: boolean;
  exists: boolean;
  updated_at: string | null;
}

/** Вкладка «Баннеры» (ТЗ-05 §6): загрузка картинки экрана со сбросом кэша file_id. */
function BannersTab() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const q = useQuery<{ items: BannerItem[] }>({
    queryKey: ["banners"],
    queryFn: () => api("/api/admin/banners"),
  });

  const upload = async (key: string, file: File) => {
    setBusy(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api(`/api/admin/banners/${key}`, { method: "POST", body: fd });
      popup({ tone: "success", title: "Баннер обновлён", body: "Появится в боте за ~15 секунд." });
      qc.invalidateQueries({ queryKey: ["banners"] });
    } catch (e) {
      popup({ tone: "error", title: "Загрузка", body: (e as Error).message });
    } finally {
      setBusy(null);
    }
  };

  const reset = async (key: string) => {
    if (!(await confirmPopup({ title: "Вернуть заводской баннер?", body: "Загруженный файл будет удалён." })))
      return;
    setBusy(key);
    try {
      await api(`/api/admin/banners/${key}`, { method: "DELETE" });
      qc.invalidateQueries({ queryKey: ["banners"] });
    } catch (e) {
      popup({ tone: "error", title: "Сброс", body: (e as Error).message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-zinc-500">
        Картинка экрана. Требования: JPEG или PNG, ровно <b className="text-zinc-300">1920×1080</b>,
        до 5 МБ. Загруженные баннеры переживают деплой и заменяют заводские.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
        {(q.data?.items ?? []).map((b) => (
          <div key={b.key} className="rounded-xl card-elev p-3 flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm text-zinc-200">{SCREEN_TITLES[b.key] ?? b.key}</span>
                {b.custom ? <Pill tone="success">свой</Pill> : <Pill>заводской</Pill>}
              </div>
              <code className="text-[11px] text-zinc-600">{b.key}.jpg</code>
            </div>
            <label className="shrink-0">
              <span
                className={`inline-flex items-center h-8 px-3 rounded-lg text-xs border cursor-pointer transition-all ${
                  busy === b.key
                    ? "opacity-50 pointer-events-none border-white/[0.08] text-zinc-500"
                    : "border-white/[0.08] text-zinc-300 hover:bg-white/[0.06]"
                }`}
              >
                {busy === b.key ? "…" : "Загрузить"}
              </span>
              <input
                type="file"
                accept="image/jpeg,image/png"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) upload(b.key, f);
                  e.target.value = "";
                }}
              />
            </label>
            {b.custom && (
              <Button variant="ghost" onClick={() => reset(b.key)}>
                Сбросить
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

interface Hint {
  id: number;
  title: string;
  category: string | null;
  issue: string | null;
  where: string | null;
  text: string;
  button_label: string | null;
  button_action: string | null;
  inject_link: boolean;
  is_active: boolean;
  sort_order: number;
}
interface HintOptions {
  category: Record<string, string>;
  issue: Record<string, Record<string, string>>;
  where: Record<string, string>;
}

/** Вкладка «Анкета» (ТЗ-05 §7): готовые ответы до создания обращения. */
function HintsTab() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<Hint> | null>(null);
  const q = useQuery<{ items: Hint[]; options: HintOptions }>({
    queryKey: ["support-hints"],
    queryFn: () => api("/api/admin/support-hints"),
  });
  const opts = q.data?.options;
  const refresh = () => qc.invalidateQueries({ queryKey: ["support-hints"] });

  const save = useMutation({
    mutationFn: (h: Partial<Hint>) =>
      h.id
        ? api(`/api/admin/support-hints/${h.id}`, { method: "PATCH", body: JSON.stringify(h) })
        : api("/api/admin/support-hints", { method: "POST", body: JSON.stringify(h) }),
    onSuccess: () => {
      popup({ tone: "success", title: "Сохранено", body: "Применится сразу." });
      setEditing(null);
      refresh();
    },
    onError: (e) => popup({ tone: "error", title: "Правило", body: (e as Error).message }),
  });

  const del = useMutation({
    mutationFn: (id: number) => api(`/api/admin/support-hints/${id}`, { method: "DELETE" }),
    onSuccess: refresh,
  });

  const cond = (h: Hint) => {
    const parts: string[] = [];
    if (h.category) parts.push(opts?.category[h.category] ?? h.category);
    if (h.issue) {
      const map = h.category ? opts?.issue[h.category] : undefined;
      parts.push(map?.[h.issue] ?? h.issue);
    }
    if (h.where) parts.push(opts?.where[h.where] ?? h.where);
    return parts.length ? parts.join(" + ") : "любые ответы";
  };

  return (
    <div className="space-y-3 max-w-3xl">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-zinc-500">
          Если ответы анкеты совпали с условием, бот показывает готовый ответ до шага «описание» —
          юзер решает вопрос сам. Кнопка «Всё равно создать обращение» добавляется автоматически.
        </p>
        <Button variant="primary" onClick={() => setEditing({ inject_link: false, sort_order: 0 })}>
          Добавить
        </Button>
      </div>

      {(q.data?.items ?? []).map((h) => (
        <div key={h.id} className="rounded-xl card-elev p-3.5 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm text-zinc-200 truncate">{h.title}</span>
                {h.is_active ? <Pill tone="success">вкл</Pill> : <Pill>выкл</Pill>}
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5">когда: {cond(h)}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button variant="ghost" onClick={() => setEditing(h)}>Изменить</Button>
              <Button
                variant="ghost"
                onClick={() => save.mutate({ id: h.id, is_active: !h.is_active })}
              >
                {h.is_active ? "Выключить" : "Включить"}
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  if (await confirmPopup({ title: "Удалить правило?", body: h.title }))
                    del.mutate(h.id);
                }}
              >
                Удалить
              </Button>
            </div>
          </div>
          <div className="rounded-lg bg-[#182533] border border-white/[0.05] px-3 py-2 text-xs text-zinc-300 line-clamp-3 tg-preview">
            <span dangerouslySetInnerHTML={{ __html: renderTelegramHtml(h.text) }} />
          </div>
        </div>
      ))}

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Правка правила" : "Новое правило"}
        maxWidth="max-w-2xl"
      >
        {editing && opts && (
          <div className="space-y-3">
            <Input
              placeholder="Название (видно только в админке)"
              value={editing.title ?? ""}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
            />
            <div className="grid grid-cols-3 gap-2">
              {(["category", "issue", "where"] as const).map((f) => {
                const map =
                  f === "issue"
                    ? (editing.category ? opts.issue[editing.category] : undefined) ?? {}
                    : opts[f];
                const anyLabel =
                  f === "category"
                    ? "направление: любое"
                    : f === "issue"
                      ? "проблема: любая"
                      : "где: любое";
                return (
                  <Select
                    key={f}
                    value={(editing[f] as string) ?? ""}
                    onChange={(v) => setEditing({ ...editing, [f]: v || null })}
                    placeholder={anyLabel}
                    options={[
                      { value: "", label: anyLabel },
                      ...Object.entries(map).map(([k, label]) => ({
                        value: k,
                        label: label as string,
                      })),
                    ]}
                  />
                );
              })}
            </div>
            <textarea
              rows={6}
              value={editing.text ?? ""}
              onChange={(e) => setEditing({ ...editing, text: e.target.value })}
              placeholder="Текст ответа. {link} подставит ссылку доступа юзера."
              className="w-full px-3 py-2 rounded-lg bg-black/25 border border-white/[0.08] text-sm text-zinc-100 font-mono leading-relaxed focus:outline-none focus:border-brand/60 resize-y"
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Текст кнопки (опц.)"
                value={editing.button_label ?? ""}
                onChange={(e) => setEditing({ ...editing, button_label: e.target.value })}
              />
              <Input
                placeholder="Действие: menu:vpn, menu:buy…"
                value={editing.button_action ?? ""}
                onChange={(e) => setEditing({ ...editing, button_action: e.target.value })}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-400">
              <input
                type="checkbox"
                checked={!!editing.inject_link}
                onChange={(e) => setEditing({ ...editing, inject_link: e.target.checked })}
              />
              подставлять ссылку доступа в <code className="text-brand">{"{link}"}</code>
            </label>
            <Button
              variant="primary"
              disabled={!editing.title || !editing.text || save.isPending}
              onClick={() => save.mutate(editing)}
            >
              {save.isPending ? "Сохраняю…" : "Сохранить"}
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}

interface Wave {
  id: string;
  title: string;
  text_key: string;
  hours: number;
  unit: string;
}

/** Вкладка «Отложенные» (ТЗ-05 §3.2): текст и тайминг каждой волны. */
function LifecycleTab({ items, onEdit }: { items: ContentItem[]; onEdit: (key: string) => void }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const q = useQuery<{ waves: Wave[]; renewal_reminder_days: number }>({
    queryKey: ["lifecycle"],
    queryFn: () => api("/api/admin/lifecycle"),
  });

  const save = useMutation({
    mutationFn: (v: { id: string; value: number }) =>
      api("/api/admin/lifecycle", { method: "PUT", body: JSON.stringify(v) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lifecycle"] });
      popup({ tone: "success", title: "Сохранено", body: "Новый тайминг применён." });
    },
    onError: (e) => popup({ tone: "error", title: "Тайминг", body: (e as Error).message }),
  });

  const row = (id: string, title: string, textKey: string | null, hours: number, unit: string) => {
    const val = draft[id] ?? String(hours);
    const changed = Number(val) !== hours;
    const item = textKey ? items.find((i) => i.key === textKey) : null;
    return (
      <div key={id} className="rounded-xl card-elev p-3.5 space-y-2.5">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-zinc-200">{title}</span>
          <div className="flex items-center gap-2 shrink-0">
            <input
              type="number"
              min={1}
              max={720}
              value={val}
              onChange={(e) => setDraft((d) => ({ ...d, [id]: e.target.value }))}
              className="h-8 w-20 px-2 rounded-lg bg-black/25 border border-white/[0.08] text-sm text-zinc-100 text-center focus:outline-none focus:border-brand/60"
            />
            <span className="text-xs text-zinc-500 w-28">{unit}</span>
            {changed && (
              <Button variant="primary" onClick={() => save.mutate({ id, value: Number(val) })}>
                Сохранить
              </Button>
            )}
          </div>
        </div>
        {item && (
          <button onClick={() => onEdit(item.key)} className="w-full text-left group">
            <div className="rounded-lg bg-[#182533] border border-white/[0.05] group-hover:border-brand/40 transition-colors px-3 py-2 text-xs text-zinc-300 line-clamp-3 tg-preview">
              <span
                dangerouslySetInnerHTML={{
                  __html: renderTelegramHtml(item.value ?? item.default_value),
                }}
              />
            </div>
          </button>
        )}
      </div>
    );
  };

  if (q.isLoading) return <div className="text-zinc-400">Загрузка…</div>;

  return (
    <div className="space-y-3 max-w-3xl">
      <p className="text-sm text-zinc-500">
        Автоматические сообщения. Клик по тексту открывает редактор. Планировщик тикает раз в
        5 минут — тайминги мельче часа смысла не имеют.
      </p>
      {(q.data?.waves ?? []).map((w) => row(w.id, w.title, w.text_key, w.hours, w.unit))}
      {q.data &&
        row(
          "renewal_reminder_days",
          "Напоминание об истечении подписки",
          "access.expiring_soon",
          q.data.renewal_reminder_days,
          "дн. до конца",
        )}
    </div>
  );
}

export default function BotContent() {
  const [tab, setTab] = useState<"screens" | "banners" | "lifecycle" | "hints" | "all">("screens");
  const [lang, setLang] = useState("ru");
  const [q, setQ] = useState("");
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [onlyOverridden, setOnlyOverridden] = useState(false);
  const [editing, setEditing] = useState<ContentItem | null>(null);

  const query = useQuery<ContentList>({
    queryKey: ["bot-content", lang],
    queryFn: () => api<ContentList>(`/api/admin/bot-content?lang=${lang}`),
  });
  const items = query.data?.items ?? [];
  const overriddenCount = items.filter((i) => i.overridden).length;

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = items.filter((i) => {
      if (onlyOverridden && !i.overridden) return false;
      if (!needle) return true;
      return i.key.toLowerCase().includes(needle) || (i.value ?? i.default_value).toLowerCase().includes(needle);
    });
    const map = new Map<string, ContentItem[]>();
    for (const it of filtered) {
      const g = groupOf(it.key);
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(it);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [items, q, onlyOverridden]);

  const cur = activeGroup && groups.find(([g]) => g === activeGroup) ? activeGroup : groups[0]?.[0] ?? null;
  const curList = groups.find(([g]) => g === cur)?.[1] ?? [];

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title="Контент бота"
        right={
          <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white/[0.03] border border-white/[0.06]">
            {(query.data?.langs ?? ["ru", "en"]).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`h-8 px-4 rounded-lg text-sm font-medium uppercase transition-all ${lang === l ? "bg-brand-soft text-brand glow-ring" : "text-zinc-400 hover:text-zinc-200"}`}
              >
                {l}
              </button>
            ))}
          </div>
        }
      />

      <p className="text-sm text-zinc-500 max-w-3xl mb-4">
        Тексты и кнопки бота. Правки применяются в боте за ~15 секунд. Плейсхолдеры{" "}
        <code className="text-brand">{"{name}"}</code> и HTML-теги{" "}
        <code className="text-brand">{"<b>"}</code> оставляй как есть — при сохранении они проверяются.
      </p>

      <div className="inline-flex items-center gap-1 p-1 mb-5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
        {([["screens", "Экраны"], ["banners", "Баннеры"], ["lifecycle", "Отложенные"], ["hints", "Анкета"], ["all", "Все тексты"]] as const).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`h-8 px-4 rounded-md text-sm font-medium transition-all ${
              tab === k ? "bg-brand-soft text-brand glow-ring" : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "screens" && (
        <ScreensTab
          onEdit={(key) => {
            const it = items.find((i) => i.key === key);
            if (it) setEditing(it);
          }}
        />
      )}

      {tab === "banners" && <BannersTab />}

      {tab === "hints" && <HintsTab />}

      {tab === "lifecycle" && (
        <LifecycleTab
          items={items}
          onEdit={(key) => {
            const it = items.find((i) => i.key === key);
            if (it) setEditing(it);
          }}
        />
      )}

      {tab === "all" && (
      <>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="w-full sm:w-72">
          <Input placeholder="Поиск по ключу или тексту…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button
          onClick={() => setOnlyOverridden((v) => !v)}
          className={`h-9 px-3.5 rounded-lg text-sm border transition-all ${onlyOverridden ? "bg-brand-soft text-brand border-transparent glow-ring" : "border-white/[0.08] text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.04]"}`}
        >
          только изменённые
        </button>
        <span className="text-xs text-zinc-500">изменено <b className="text-brand">{overriddenCount}</b> из {items.length}</span>
      </div>

      {query.isLoading && <div className="text-zinc-400">Загрузка…</div>}

      {/* вкладки-секции */}
      {groups.length > 0 && (
        <div className="flex items-center gap-1.5 mb-4 flex-wrap">
          {groups.map(([g, list]) => (
            <button
              key={g}
              onClick={() => setActiveGroup(g)}
              className={`h-8 px-3 rounded-lg text-xs font-medium border transition-all ${g === cur ? "bg-brand-soft text-brand border-brand/40 glow-ring" : "bg-black/20 border-white/[0.08] text-zinc-400 hover:text-zinc-200"}`}
            >
              {GROUP_TITLES[g] ?? g} <span className="opacity-50">{list.length}</span>
            </button>
          ))}
        </div>
      )}

      {/* компактный список секции */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
        {curList.map((it) => {
          const val = it.value ?? it.default_value;
          const btn = isButton(it.key, val);
          return (
            <div key={it.key} className="rounded-xl card-elev p-3 flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <code className="text-[11px] font-mono text-zinc-500 truncate">{it.key}</code>
                  {it.overridden ? <Pill tone="success">изменено</Pill> : <Pill>заводской</Pill>}
                </div>
                {btn ? (
                  <TgPreview text={val} btn />
                ) : (
                  <div className="text-sm text-zinc-300 line-clamp-2 break-words" dangerouslySetInnerHTML={{ __html: renderTelegramHtml(val) }} />
                )}
              </div>
              <Button variant="ghost" onClick={() => setEditing(it)}>
                Изменить
              </Button>
            </div>
          );
        })}
        {!query.isLoading && curList.length === 0 && <div className="text-zinc-600 text-sm">Ничего не найдено.</div>}
      </div>
      </>
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing ? `Правка · ${editing.key}` : ""} maxWidth="max-w-3xl">
        {editing && <EditContent item={editing} lang={lang} onDone={() => setEditing(null)} />}
      </Modal>
    </div>
  );
}

function EditContent({ item, lang, onDone }: { item: ContentItem; lang: string; onDone: () => void }) {
  const qc = useQueryClient();
  const saved = item.value ?? item.default_value; // «как было» — текущее в боте
  const [text, setText] = useState(saved);
  const [media, setMedia] = useState(item.media_url ?? "");
  const btn = isButton(item.key, text);
  const dirty = text !== saved || (media || null) !== (item.media_url ?? null);
  const rows = Math.min(16, Math.max(3, text.split("\n").length));

  const save = useMutation({
    mutationFn: () =>
      api("/api/admin/bot-content", {
        method: "PUT",
        body: JSON.stringify({ key: item.key, lang, value: text, media_url: media || null }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bot-content", lang] });
      popup({ tone: "success", title: "Сохранено", body: "Применится в боте за ~15 секунд." });
      onDone();
    },
    onError: (e) => popup({ tone: "error", title: "Сохранение", body: (e as Error).message }),
  });
  const reset = useMutation({
    mutationFn: () =>
      api("/api/admin/bot-content/reset", { method: "POST", body: JSON.stringify({ key: item.key, lang }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bot-content", lang] });
      popup({ tone: "success", title: "Сброшено на заводской" });
      onDone();
    },
    onError: (e) => popup({ tone: "error", title: "Сброс", body: (e as Error).message }),
  });

  // История версий для отката (ТЗ-05 §5) — грузим только для изменённых ключей.
  const history = useQuery<{ items: { id: number; value: string | null; changed_at: string }[] }>({
    queryKey: ["bot-content-history", item.key, lang],
    queryFn: () =>
      api(`/api/admin/bot-content/history?key=${encodeURIComponent(item.key)}&lang=${lang}`),
    enabled: item.overridden,
  });
  const rollback = useMutation({
    mutationFn: (id: number) =>
      api(`/api/admin/bot-content/rollback/${id}`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bot-content", lang] });
      qc.invalidateQueries({ queryKey: ["bot-content-screens"] });
      popup({ tone: "success", title: "Откат выполнен", body: "Вернули прошлую версию." });
      onDone();
    },
    onError: (e) => popup({ tone: "error", title: "Откат", body: (e as Error).message }),
  });

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs text-zinc-500 mb-1">Текст {item.overridden ? "(изменённый)" : "(заводской)"}</div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={rows}
          spellCheck={false}
          autoFocus
          className="w-full px-3 py-2 rounded-lg bg-black/25 border border-white/[0.08] text-sm text-zinc-100 font-mono leading-relaxed focus:outline-none focus:border-brand/60 focus:ring-2 focus:ring-brand/20 transition resize-y"
        />
      </div>

      {!btn && (
        <Input value={media} onChange={(e) => setMedia(e.target.value)} placeholder="баннер: URL картинки или file_id (опц.)" className="h-8 font-mono text-xs" />
      )}

      {(history.data?.items?.length ?? 0) > 0 && (
        <details className="rounded-lg border border-white/[0.07] bg-black/20">
          <summary className="cursor-pointer select-none px-3 py-2 text-xs text-zinc-400 hover:text-zinc-200">
            История правок ({history.data!.items.length}) — можно откатить
          </summary>
          <div className="px-3 pb-3 space-y-2">
            {history.data!.items.map((h) => (
              <div key={h.id} className="flex items-start gap-3 text-xs">
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-500 mb-0.5">
                    {new Date(h.changed_at).toLocaleString("ru-RU")}
                  </div>
                  <div className="text-zinc-300 line-clamp-2 break-words font-mono">
                    {h.value ?? "— (заводской)"}
                  </div>
                </div>
                <Button variant="ghost" onClick={() => rollback.mutate(h.id)}>
                  Вернуть
                </Button>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* было / стало */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-zinc-600 mb-1.5">как было</div>
          <TgPreview text={saved} media={item.media_url} btn={isButton(item.key, saved)} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-brand/80 mb-1.5">как станет</div>
          <TgPreview text={text} media={media} btn={btn} />
        </div>
      </div>

      <div className="flex items-center gap-3 justify-end pt-1">
        {item.overridden && (
          <Button
            variant="ghost"
            disabled={reset.isPending}
            onClick={async () => {
              if (await confirmPopup({ title: "Вернуть заводской текст?", body: item.key, confirmLabel: "Сбросить" })) reset.mutate();
            }}
          >
            {reset.isPending ? "…" : "Сбросить на заводской"}
          </Button>
        )}
        <Button variant="primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Сохраняю…" : "Сохранить"}
        </Button>
      </div>
    </div>
  );
}
